/**
 * ConcentricZonesViz component.
 *
 * SVG-based 4-ring concentric visualization representing the four security tiers.
 * Each ring's stroke color and opacity saturates based on device count in that tier.
 *
 * Rendering rules (design spec section 3.2):
 * - Baseline (calm): rings are barely visible (opacity 0.35), breathing at 2s intervals
 * - Activity saturation: 1 device = faint, 3+ = full cyan, 5+ = amber
 * - Sensor dots ride just outside the outer ring; colored by health
 * - Hover on ring: peek panel with zone name + device count + turned-away count
 * - Hover on sensor: tooltip with chip_id + friendly name + heartbeat + packet rate
 *
 * Alert ring animations: AlertRing components rendered from activeAlertRings
 * prop (Plan 03-06 live implementation).
 *
 * prefers-reduced-motion: ring breathing + dot pulse + offline rotation are
 * all disabled; ring saturation color changes remain (functional, not decorative).
 */
import { motion } from 'framer-motion';
import * as HoverCard from '@radix-ui/react-hover-card';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  getRingRadius,
  getRingSaturationColor,
  getRingOpacity,
  getSensorDotPosition,
} from './concentric-geometry';
import { tokenValues } from '@/design/tokens';
import type { ZoneSnapshot, SensorSnapshot, ZoneId } from './schemas';
import { AlertRing } from './AlertRing';
import type { AlertRingEntry } from './AlertRing';
import * as styles from './ConcentricZonesViz.css';

// ---------------------------------------------------------------------------
// Types (re-export AlertRingState for backward compat)
// ---------------------------------------------------------------------------

/**
 * Per-alert animation state for the ring collapse/expand animation.
 * Plan 03-06: populated from WebSocket events via useLiveRingState.
 *
 * @deprecated Use AlertRingEntry from AlertRing.tsx directly.
 *             This alias remains for backward compatibility with plan 03-05 tests.
 */
export type AlertRingState = AlertRingEntry;

interface Props {
  /** The 4 security zones from live-state. Always exactly 4 in Phase 3. */
  zones: ZoneSnapshot[];
  /** Sensor nodes for health display. */
  sensors: SensorSnapshot[];
  /**
   * Device counts per ring tier.
   * Phase 03-05 ships all zeros (baseline rendering).
   * Plan 03-06 computes from WebSocket detections.
   */
  deviceCountPerRing: Record<ZoneId, number>;
  /**
   * Active alert ring animation states.
   * Plan 03-06: populated from useLiveRingState via WebSocket events.
   */
  activeAlertRings: AlertRingEntry[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VIEW_BOX_SIZE = 600;
const CENTER = VIEW_BOX_SIZE / 2;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * A single concentric ring circle with breathing animation and hover peek panel.
 */
function Ring({
  zone,
  deviceCount,
  reducedMotion,
}: {
  zone: ZoneSnapshot;
  deviceCount: number;
  reducedMotion: boolean;
}) {
  const radius = getRingRadius(VIEW_BOX_SIZE, zone.tier as 1 | 2 | 3 | 4);
  const color = getRingSaturationColor(deviceCount, tokenValues);
  const opacity = getRingOpacity(deviceCount);
  const isAmber = deviceCount >= 5;

  // Breathing animation: 2s opacity sine between baseline and baseline+0.25.
  // Disabled when devices are present (the saturation level communicates state)
  // or when user prefers reduced motion.
  const shouldBreathe = !reducedMotion && deviceCount === 0;

  const circleProps = {
    cx: CENTER,
    cy: CENTER,
    r: radius,
    stroke: color,
    strokeWidth: 2,
    fill: 'none',
    opacity,
    style: { transition: 'stroke 200ms ease, opacity 200ms ease' },
  };

  return (
    <HoverCard.Root openDelay={200} closeDelay={100}>
      <HoverCard.Trigger asChild>
        <g
          data-testid={`ring-${zone.id}`}
          data-device-count={String(deviceCount)}
          data-color={isAmber ? 'warn' : 'cyan'}
          style={{ cursor: 'default' }}
        >
          {shouldBreathe ? (
            <motion.circle
              {...circleProps}
              animate={{
                opacity: [opacity, Math.min(1, opacity + 0.25), opacity],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          ) : (
            <circle {...circleProps} />
          )}
          {/* Invisible hit area wider than the stroke for easier hover */}
          <circle
            cx={CENTER}
            cy={CENTER}
            r={radius}
            stroke="transparent"
            strokeWidth={16}
            fill="none"
          />
        </g>
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side="right"
          sideOffset={8}
          style={{
            background: tokenValues.colors.bg.surface2,
            border: `1px solid ${tokenValues.colors.border.subtle}`,
            borderRadius: tokenValues.radii.md,
            padding: '10px 14px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            minWidth: '160px',
          }}
        >
          <div
            style={{
              fontFamily: tokenValues.fonts.sans,
              fontSize: '12px',
              color: tokenValues.colors.text.secondary,
              marginBottom: '6px',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {zone.name}
          </div>
          <div
            style={{
              fontFamily: tokenValues.fonts.mono,
              fontSize: '13px',
              color: deviceCount > 0 ? color : tokenValues.colors.text.tertiary,
            }}
          >
            {deviceCount} device{deviceCount !== 1 ? 's' : ''}
          </div>
          <HoverCard.Arrow
            style={{ fill: tokenValues.colors.border.subtle }}
          />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}

/**
 * A single sensor health dot positioned around the outer ring.
 */
function SensorDot({
  sensor,
  index,
  total,
  outerRadius,
  reducedMotion,
}: {
  sensor: SensorSnapshot;
  index: number;
  total: number;
  outerRadius: number;
  reducedMotion: boolean;
}) {
  const pos = getSensorDotPosition(VIEW_BOX_SIZE, index, total, outerRadius);

  // Color by health status
  const color =
    sensor.health === 'healthy'
      ? tokenValues.colors.status.ok
      : sensor.health === 'stale'
        ? tokenValues.colors.status.warn
        : tokenValues.colors.status.alert;

  // Format last heartbeat as relative time (simple version; dayjs would add bundle)
  const heartbeatLabel = sensor.last_heartbeat_at
    ? new Date(sensor.last_heartbeat_at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'never';

  const shouldPulse = !reducedMotion && sensor.health === 'healthy';

  return (
    <Tooltip.Provider>
      <Tooltip.Root delayDuration={200}>
        <Tooltip.Trigger asChild>
          <g
            data-testid={`sensor-dot-${sensor.chip_id}`}
            data-health={sensor.health}
          >
            {shouldPulse ? (
              <motion.circle
                cx={pos.x}
                cy={pos.y}
                r={4}
                fill={color}
                animate={{ opacity: [1, 0.4, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
              />
            ) : (
              <circle cx={pos.x} cy={pos.y} r={4} fill={color} opacity={0.85} />
            )}
            {/* Offline indicator: a small dashed ring overlay */}
            {sensor.health === 'offline' && !reducedMotion && (
              <motion.circle
                cx={pos.x}
                cy={pos.y}
                r={7}
                stroke={color}
                strokeWidth={1.5}
                strokeDasharray="3 2"
                fill="none"
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                style={{ transformOrigin: `${pos.x}px ${pos.y}px` }}
              />
            )}
          </g>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            sideOffset={6}
            style={{
              background: tokenValues.colors.bg.surface2,
              border: `1px solid ${tokenValues.colors.border.subtle}`,
              borderRadius: tokenValues.radii.md,
              padding: '8px 12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              maxWidth: '200px',
            }}
          >
            <div
              style={{
                fontFamily: tokenValues.fonts.mono,
                fontSize: '11px',
                color: tokenValues.colors.accent.gold,
                marginBottom: '3px',
                letterSpacing: '0.02em',
              }}
            >
              {sensor.chip_id}
            </div>
            {sensor.friendly_name && (
              <div
                style={{
                  fontFamily: tokenValues.fonts.sans,
                  fontSize: '12px',
                  color: tokenValues.colors.text.primary,
                  marginBottom: '3px',
                }}
              >
                {sensor.friendly_name}
              </div>
            )}
            <div
              style={{
                fontFamily: tokenValues.fonts.sans,
                fontSize: '11px',
                color: tokenValues.colors.text.tertiary,
              }}
            >
              Last heartbeat: {heartbeatLabel}
            </div>
            <div
              style={{
                fontFamily: tokenValues.fonts.mono,
                fontSize: '11px',
                color: tokenValues.colors.text.secondary,
                marginTop: '2px',
              }}
            >
              {sensor.packet_rate.toFixed(1)} pkt/s
            </div>
            <Tooltip.Arrow style={{ fill: tokenValues.colors.border.subtle }} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

/**
 * Legend strip below the visualization.
 * Shows the 4 outcome symbols with text labels.
 */
function Legend() {
  return (
    <div className={styles.legend} data-testid="viz-legend">
      <span className={styles.legendItem}>
        <span style={{ color: tokenValues.colors.accent.goldDim }}>○</span>
        <span>calm</span>
      </span>
      <span className={styles.legendItem}>
        <span style={{ color: tokenValues.colors.accent.gold }}>⊙</span>
        <span>approaching</span>
      </span>
      <span className={styles.legendItem}>
        <span style={{ color: tokenValues.colors.status.ok }}>↺</span>
        <span>turned away</span>
      </span>
      <span className={styles.legendItem}>
        <span style={{ color: tokenValues.colors.status.alert }}>✗</span>
        <span>entered</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * ConcentricZonesViz: the SVG-based concentric ring visualization.
 *
 * @param zones - 4-element array from live-state; always in Phase 3
 * @param sensors - Sensor health array; may be empty before first heartbeat
 * @param deviceCountPerRing - Per-tier device counts; all zero in Phase 03-05
 * @param activeAlertRings - Alert animation states; always empty in Phase 03-05
 */
export function ConcentricZonesViz({
  zones,
  sensors,
  deviceCountPerRing,
  activeAlertRings,
}: Props) {
  const reducedMotion = useReducedMotion();
  const outerRadius = getRingRadius(VIEW_BOX_SIZE, 1);

  // Sort zones from outer (tier 1) to inner (tier 4) for correct draw order
  const sortedZones = [...zones].sort((a, b) => b.tier - a.tier);

  return (
    <div className={styles.container}>
      <svg
        viewBox={`0 0 ${VIEW_BOX_SIZE} ${VIEW_BOX_SIZE}`}
        className={styles.svg}
        preserveAspectRatio="xMidYMid meet"
        aria-label="Concentric zones visualization"
        role="img"
      >
        {/* Draw rings from inner to outer so outer appears on top visually */}
        {sortedZones.map((zone) => (
          <Ring
            key={zone.id}
            zone={zone}
            deviceCount={deviceCountPerRing[zone.id] ?? 0}
            reducedMotion={reducedMotion}
          />
        ))}

        {/* Sensor dots around the outer ring */}
        {sensors.map((s, i) => (
          <SensorDot
            key={s.chip_id}
            sensor={s}
            index={i}
            total={sensors.length}
            outerRadius={outerRadius}
            reducedMotion={reducedMotion}
          />
        ))}

        {/* Alert ring animations (Plan 03-06): inward-collapse, expand-fade, color journey */}
        {activeAlertRings.map((a) => (
          <AlertRing key={a.alertId} state={a} reducedMotion={reducedMotion} />
        ))}
      </svg>

      {/* Waiting message when no sensors are yet reporting */}
      {sensors.length === 0 && (
        <p
          className={styles.waitingMessage}
          aria-live="polite"
        >
          Waiting for first heartbeat...
        </p>
      )}

      <Legend />
    </div>
  );
}
